// account-auth.js — Connected accounts (OAuth) + MFA + recovery
// codes panel on the Account page.
//
// Public exports (consumed by pages/account.js via AC_LEGACY
// bridge re-exports):
//   refreshConnectedAccounts()
//   refreshAccountMfaStatus()
//   refreshRecoveryCodesStatus()
//
// Module-internal:
//   CONNECTED_PROVIDERS — the OAuth provider list (currently just
//                          GitHub; add Google here when enabled
//                          server-side)
//   linkProvider, unlinkProvider — Supabase identity link flows
//   generateAndShowRecoveryCodes — POST to /v1/user/mfa/recovery-codes,
//                                  then show the codes modal
//   wireConnectedAccounts — module-init listener wiring for the
//                            panel's Refresh button + the
//                            Connect/Disconnect button delegation
//   (a click handler for the regenerate-codes button is also wired
//   at module init)
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2).
import { $ } from "./dom.js";
import { escapeHTML } from "./util.js";
import { showToast, showConfirm } from "./ui.js";
import { showRecoveryCodesModal } from "./recovery-codes-modal.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

const CONNECTED_PROVIDERS = [
  {
    id: "github",
    label: "GitHub",
    // SVG path lifted from the auth-screen button. Kept inline so
    // there's no asset dependency.
    icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .5a11.5 11.5 0 0 0-3.63 22.41c.58.1.79-.25.79-.55 0-.27-.01-1.16-.02-2.11-3.21.7-3.89-1.37-3.89-1.37-.52-1.33-1.27-1.69-1.27-1.69-1.04-.71.08-.7.08-.7 1.15.08 1.75 1.18 1.75 1.18 1.03 1.75 2.69 1.25 3.34.96.1-.75.4-1.25.73-1.54-2.56-.29-5.26-1.28-5.26-5.68 0-1.26.45-2.28 1.18-3.09-.12-.29-.51-1.46.11-3.05 0 0 .96-.31 3.15 1.18a10.97 10.97 0 0 1 5.74 0c2.19-1.49 3.15-1.18 3.15-1.18.62 1.59.23 2.76.12 3.05.74.81 1.18 1.83 1.18 3.09 0 4.41-2.7 5.38-5.27 5.67.41.36.78 1.06.78 2.14 0 1.55-.01 2.79-.01 3.17 0 .31.21.66.79.55A11.5 11.5 0 0 0 12 .5Z"/></svg>',
  },
];

export async function refreshConnectedAccounts() {
  const body = $("connected-accounts-body");
  if (!body) return;
  const supabase = getSupabase();
  if (!supabase) {
    body.innerHTML =
      '<div class="setting-row" style="border-bottom: none; opacity: 0.7;"><div class="setting-info"><div class="setting-desc">Connected accounts only apply in cloud mode.</div></div></div>';
    return;
  }

  let identities = [];
  let err = "";
  try {
    const resp = await supabase.auth.getUserIdentities();
    if (resp.error) throw resp.error;
    identities = (resp.data && resp.data.identities) || [];
  } catch (e) {
    err = e.message || String(e);
  }

  if (err) {
    body.innerHTML =
      '<div class="setting-row" style="border-bottom: none;"><div class="setting-info"><div class="setting-desc" style="color: var(--danger);">Couldn’t load connected accounts: ' +
      escapeHTML(err) +
      "</div></div></div>";
    return;
  }

  // Index by provider for quick lookup. A user can technically have
  // multiple identities of the same provider in edge cases (e.g.
  // merged accounts); we render the first match and stash the rest
  // for the unlink handler to pick by id.
  const byProvider = {};
  for (const id of identities) {
    if (!byProvider[id.provider]) byProvider[id.provider] = id;
  }

  let html = "";
  for (const p of CONNECTED_PROVIDERS) {
    const linked = byProvider[p.id];
    const metadata = (linked && linked.identity_data) || {};
    const username =
      metadata.user_name ||
      metadata.preferred_username ||
      metadata.name ||
      metadata.email ||
      "";
    const otherCount = identities.length - 1;
    const unlinkable = linked && otherCount > 0;

    html +=
      '<div class="setting-row"' +
      (p === CONNECTED_PROVIDERS[CONNECTED_PROVIDERS.length - 1]
        ? ' style="border-bottom: none;"'
        : "") +
      ">";
    html +=
      '<div class="setting-info" style="display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0;">';
    html +=
      '<span style="display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; background: var(--bg-elevated); color: var(--text); flex-shrink: 0;">' +
      p.icon +
      "</span>";
    html += '<div style="min-width: 0;">';
    html += '<div class="setting-label">' + escapeHTML(p.label) + "</div>";
    if (linked) {
      html +=
        '<div class="setting-desc" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Connected' +
        (username ? " as <strong>" + escapeHTML(username) + "</strong>" : "") +
        "</div>";
    } else {
      html +=
        '<div class="setting-desc">Not connected. Link to sign in with ' +
        escapeHTML(p.label) +
        ".</div>";
    }
    html += "</div></div>";
    if (linked) {
      const disabledAttr = unlinkable
        ? ""
        : ' disabled style="opacity: 0.5; cursor: not-allowed;"';
      const tip = unlinkable
        ? "Disconnect " + p.label + " from this account"
        : "Cannot disconnect " +
          p.label +
          " — it’s the only way to sign in. Add another method first.";
      html +=
        '<button class="btn btn-sm btn-ghost" data-unlink-provider="' +
        escapeHTML(p.id) +
        '" data-tooltip="' +
        escapeHTML(tip) +
        '"' +
        disabledAttr +
        ">Disconnect</button>";
    } else {
      html +=
        '<button class="btn btn-sm" data-link-provider="' +
        escapeHTML(p.id) +
        '" data-tooltip="Link this provider to your account">Connect</button>';
    }
    html += "</div>";
  }

  body.innerHTML = html;
  // Stash the loaded identities so the unlink handler can resolve
  // a provider name to its specific identity row id (Supabase
  // unlinkIdentity wants the full identity object).
  body._loadedIdentities = identities;
}

async function linkProvider(provider, btn) {
  const supabase = getSupabase();
  if (!supabase) return;
  if (btn) {
    btn.disabled = true;
    btn.style.opacity = "0.7";
    btn.textContent = "Redirecting…";
  }
  try {
    // linkIdentity (vs signInWithOAuth) attaches the new provider
    // to the *current* user instead of creating a new auth row.
    // Requires the user to already be signed in, which they are
    // since the Account page is gated behind handleAuthSuccess.
    const { error } = await supabase.auth.linkIdentity({
      provider,
      options: { redirectTo: window.location.origin + "/account" },
    });
    if (error) throw error;
    // Success → browser navigates away to the provider's
    // authorization page. The user returns signed in with the new
    // identity attached.
  } catch (e) {
    showToast("error", "Connect failed: " + (e.message || e));
    if (btn) {
      btn.disabled = false;
      btn.style.opacity = "";
      btn.textContent = "Connect";
    }
  }
}

async function unlinkProvider(provider, btn) {
  const supabase = getSupabase();
  if (!supabase) return;
  const body = $("connected-accounts-body");
  const identities = (body && body._loadedIdentities) || [];
  const target = identities.find((i) => i.provider === provider);
  if (!target) {
    showToast("error", "Could not find that provider on your account.");
    return;
  }
  const ok = await showConfirm(
    "Disconnect " + provider + "?",
    "You will no longer be able to sign in via " +
      provider +
      ". You can re-connect later.",
    "Disconnect",
  );
  if (!ok) return;
  if (btn) {
    btn.disabled = true;
    btn.style.opacity = "0.7";
    btn.textContent = "Disconnecting…";
  }
  try {
    const { error } = await supabase.auth.unlinkIdentity(target);
    if (error) throw error;
    showToast("success", provider + " disconnected.");
    await refreshConnectedAccounts();
  } catch (e) {
    showToast("error", "Disconnect failed: " + (e.message || e));
    if (btn) {
      btn.disabled = false;
      btn.style.opacity = "";
      btn.textContent = "Disconnect";
    }
  }
}

export async function refreshAccountMfaStatus() {
  const supabase = getSupabase();
  if (!supabase) return;
  const label = $("account-mfa-label");
  const desc = $("account-mfa-desc");
  const btn = $("account-mfa-btn");
  const codesRow = $("account-recovery-codes-row");
  if (!label || !desc || !btn) return;
  try {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    const totp = (data && data.totp) || [];
    const verified = totp.find((f) => f.status === "verified");
    if (verified) {
      label.textContent = "Two-factor authentication is enabled";
      desc.textContent =
        "You will be asked for a 6-digit code each time you sign in.";
      btn.textContent = "Disable 2FA";
      btn.style.color = "var(--danger)";
      btn.style.borderColor = "var(--danger)";
      btn.dataset.factorId = verified.id;
      btn.dataset.mode = "disable";
      if (codesRow) codesRow.style.display = "";
    } else {
      label.textContent = "Enable two-factor authentication";
      desc.textContent =
        "Add an extra layer of security. You’ll be asked for a 6-digit code from an authenticator app (Authy, 1Password, Google Authenticator) each time you sign in.";
      btn.textContent = "Enable 2FA";
      btn.style.color = "";
      btn.style.borderColor = "";
      delete btn.dataset.factorId;
      btn.dataset.mode = "enable";
      if (codesRow) codesRow.style.display = "none";
      // Also clean up any unverified factors left behind from abandoned enrollments.
      const unverified = totp.filter((f) => f.status !== "verified");
      for (const f of unverified) {
        try {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        } catch {}
      }
    }
  } catch (e) {
    desc.textContent = "Could not load 2FA status: " + (e.message || "unknown");
  }
}

// Recovery-codes count — rendered in the 2FA panel beneath the enable/disable row.
export async function refreshRecoveryCodesStatus() {
  const desc = $("account-recovery-codes-desc");
  const regenBtn = $("account-regen-codes-btn");
  const row = $("account-recovery-codes-row");
  if (!desc || !regenBtn || !row || row.style.display === "none") return;
  const supabase = getSupabase();
  if (!supabase) {
    desc.textContent = "Sign in to view recovery codes.";
    return;
  }
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      desc.textContent = "Sign in to view recovery codes.";
      return;
    }
    const r = await fetch(COORDINATOR + "/v1/user/mfa/recovery-codes", {
      headers: { Authorization: "Bearer " + session.access_token },
    });
    if (!r.ok) {
      desc.textContent = "Could not load recovery codes status.";
      return;
    }
    const body = await r.json();
    const rem = Number(body.remaining || 0);
    const tot = Number(body.total || 10);
    if (rem < 3) {
      desc.innerHTML =
        '<span style="color: var(--danger); font-weight: 600;">' +
        rem +
        " of " +
        tot +
        " recovery codes remaining.</span> Regenerate new codes &mdash; old codes stop working.";
    } else {
      desc.textContent =
        rem + " of " + tot + " recovery codes remaining. Keep them somewhere safe.";
    }
  } catch (e) {
    desc.textContent = "Could not load recovery codes status.";
  }
}

// Generate new recovery codes (called right after MFA enrollment succeeds,
// and when the user clicks "Regenerate codes" from the 2FA panel).
async function generateAndShowRecoveryCodes(btn) {
  const supabase = getSupabase();
  if (!supabase) return;
  const prevText = btn ? btn.textContent : null;
  if (btn) {
    btn.disabled = true;
    btn.textContent = "Generating…";
  }
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in.");
    const r = await fetch(COORDINATOR + "/v1/user/mfa/recovery-codes", {
      method: "POST",
      headers: { Authorization: "Bearer " + session.access_token },
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      throw new Error(
        e.message || e.error || "Could not generate recovery codes.",
      );
    }
    const body = await r.json();
    const codes = Array.isArray(body.codes) ? body.codes : [];
    if (!codes.length) throw new Error("Coordinator returned no codes.");
    await showRecoveryCodesModal(codes);
    await refreshRecoveryCodesStatus();
  } catch (e) {
    showToast(
      "error",
      "Failed to generate recovery codes: " + (e.message || "unknown"),
    );
  } finally {
    if (btn) {
      btn.disabled = false;
      if (prevText) btn.textContent = prevText;
    }
  }
}

// ---- module init: wire the panel's listeners ----

function wireListeners() {
  // Connected accounts panel — refresh + delegated link/unlink clicks.
  const refreshBtn = $("connected-accounts-refresh");
  if (refreshBtn) {
    refreshBtn.addEventListener("click", async () => {
      refreshBtn.disabled = true;
      try {
        await refreshConnectedAccounts();
        showToast("success", "Refreshed connected accounts.");
      } catch (e) {
        showToast("error", "Refresh failed: " + (e.message || e));
      } finally {
        refreshBtn.disabled = false;
      }
    });
  }
  const body = $("connected-accounts-body");
  if (body) {
    body.addEventListener("click", async (e) => {
      const linkBtn = e.target.closest("[data-link-provider]");
      if (linkBtn) {
        const provider = linkBtn.dataset.linkProvider;
        await linkProvider(provider, linkBtn);
        return;
      }
      const unlinkBtn = e.target.closest("[data-unlink-provider]");
      if (unlinkBtn && !unlinkBtn.disabled) {
        const provider = unlinkBtn.dataset.unlinkProvider;
        await unlinkProvider(provider, unlinkBtn);
        return;
      }
    });
  }

  // Regenerate-codes button: warn before regenerating (invalidates existing).
  const regenBtn = $("account-regen-codes-btn");
  if (regenBtn) {
    regenBtn.addEventListener("click", async () => {
      const ok = await showConfirm(
        "Regenerate recovery codes?",
        "Your existing recovery codes will stop working. You’ll see a fresh set of 10 codes &mdash; make sure to save them.",
        "Regenerate",
      );
      if (!ok) return;
      await generateAndShowRecoveryCodes(regenBtn);
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
